import { Button, Card, StatusChip, type StatusTone } from "@prisma/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Check,
  ChevronRight,
  CircleCheck,
  Circle,
  Download,
  Eye,
  FileText,
  Film,
  MessageSquare,
  Pencil,
  RotateCcw,
  XCircle,
} from "lucide-react";
import { useRef, useState, type ReactNode, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams, useSearchParams } from "react-router";

import { Dialog } from "../../components/Dialog";
import { PageHeader } from "../../components/PageHeader";
import { QueryState } from "../../components/QueryState";
import { api, ApiError, call } from "../../lib/api";
import type { components } from "../../lib/api-types";
import { formatBytes, formatDateTime, formatRelative, secondsSince } from "../../lib/format";
import { useSession } from "../../lib/session";

type Row = components["schemas"]["ReviewRowOut"];
type WS = components["schemas"]["ReviewWorkspaceOut"];
type CommentT = components["schemas"]["CommentOut"];

const A = { open: "openReview", approve: "approve", revision: "requestRevision", reject: "reject", comment: "comment",
  reply: "reply", tick: "tick", notes: "editNotes" } as const;
const REVISION_REASONS = ["HOOK", "PACING", "PRODUCT_VISIBILITY", "CTA", "CLAIMS", "BRAND", "TECHNICAL", "OTHER"] as const;
const REJECT_REASONS = ["OFF_BRIEF", "CLAIMS_RISK", "QUALITY", "WRONG_FORMAT", "OTHER"] as const;
const STATUS_TONE: Record<string, StatusTone> = { OPEN: "review", APPROVED: "success", REVISION_REQUIRED: "warning", REJECTED: "danger" };
const CLAIMS = "claims_and_compliance";
const QUALITIES = ["proxy", "original"] as const;

export function timecode(ms: number | null | undefined): string {
  if (ms == null) return "";
  const s = Math.floor(ms / 1000);
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

function ErrorLine({ error }: { error: unknown }) {
  const { t } = useTranslation();
  if (!error) return null;
  const code = error instanceof ApiError ? error.code : "unknown";
  return <p role="alert" className="text-[13px] text-[var(--status-danger)]">{t(`error.${code}`, { defaultValue: t("error.unknown") })}</p>;
}

/** /reviews: work waiting for review and recent decisions. */
export function ReviewQueuePage() {
  const { t, i18n } = useTranslation();
  const q = useQuery({ queryKey: ["reviews"], queryFn: () => call<Row[]>(api.GET("/app/v1/reviews")) });
  return (
    <div className="space-y-6">
      <PageHeader titleKey="review.queue_title" subtitle={t("review.queue_subtitle")} />
      <QueryState query={q} emptyKey="review.queue_empty">
        {(rows) => (
          <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {rows.map((r) => (
              <li key={r.display_id}>
                <Link to={`/projects/${r.request}/reviews/${r.display_id}`} className="block overflow-hidden rounded-[var(--radius-card)] border border-line bg-surface shadow-[var(--shadow-card)] hover:border-[var(--border-strong)]">
                  <div className="relative flex aspect-video items-center justify-center bg-soft">
                    {r.thumbnail_url ? <img src={r.thumbnail_url} alt="" className="h-full w-full object-cover" loading="lazy" /> : <Film aria-hidden="true" className="size-8 text-ink-2" />}
                    <span className="absolute left-2 top-2 rounded-md bg-surface px-1.5 py-0.5 text-[11px] font-semibold text-ink">{t("editor.version_short", { n: r.version })}</span>
                  </div>
                  <div className="space-y-1 p-3 text-[13px]">
                    <div className="flex items-center justify-between gap-2"><span className="font-semibold text-ink">{r.display_id}</span>
                      <StatusChip tone={r.status === "APPROVED" ? "success" : r.status === "REJECTED" ? "danger" : r.status === "REVISION_REQUIRED" ? "warning" : "review"} icon={null} label={t(`status.production_job.${r.status.toLowerCase()}`)} /></div>
                    <div className="truncate text-ink">{r.product_name} · {r.request}</div>
                    <div className="truncate text-ink-2">{r.editor ?? ""}{r.submitted_at ? ` · ${formatRelative(secondsSince(r.submitted_at), i18n.language)}` : ""}</div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </QueryState>
    </div>
  );
}

/** Review Workspace (locked screens 04/08; ADR 0024). */
export function ReviewWorkspacePage() {
  const { jobId = "" } = useParams();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const v = params.get("v") ? Number(params.get("v")) : undefined;
  const key = ["review", jobId, v ?? "current"];
  const q = useQuery({ queryKey: key, queryFn: () => call<WS>(api.GET("/app/v1/reviews/{display_id}", { params: { path: { display_id: jobId }, query: { v } } })) });
  const refresh = () => { void qc.invalidateQueries({ queryKey: ["review", jobId] }); void qc.invalidateQueries({ queryKey: ["reviews"] }); };
  const player = useRef<HTMLVideoElement>(null);
  const [now, setNow] = useState(0);
  const open = useMutation({ mutationFn: () => call(api.POST("/app/v1/reviews/{display_id}/open", { params: { path: { display_id: jobId } } })), onSuccess: refresh });
  return (
    <QueryState query={q}>
      {(w) => {
        const can = (a: string) => w.allowed_actions.includes(a);
        const seek = (ms: number) => { if (player.current) { player.current.currentTime = ms / 1000; void player.current.play().catch(() => undefined); } };
        return (
          <div className="space-y-5">
            <nav aria-label={t("requests.breadcrumb")} className="flex items-center gap-1.5 text-[13px] text-ink-2">
              <Link to="/projects" className="hover:text-ink">{t("nav.projects")}</Link><ChevronRight aria-hidden="true" className="size-3.5" />
              <Link to={`/projects/${w.request}`} className="hover:text-ink">{w.request}</Link><ChevronRight aria-hidden="true" className="size-3.5" />
              <Link to="/reviews" className="hover:text-ink">{t("nav.reviews")}</Link>
            </nav>
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <h1 className="text-[40px] font-semibold leading-[1.1] tracking-[-0.02em] text-ink">{t("review.title_lead")} <span className="text-gradient">{t("review.title_em")}</span></h1>
                <p className="mt-1 text-[19px] text-ink-2">{w.request} / {w.product.name}{w.hypothesis ? ` · ${w.hypothesis}` : ""}</p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <p className="hidden max-w-56 text-right text-[16px] italic leading-snug text-ink-2 xl:block">{t("home.brand_line")}</p>
                <Link to={`/editor/jobs/${w.display_id}`} className="inline-flex h-10 items-center gap-2 rounded-[var(--radius-control)] border border-line bg-surface px-4 text-sm font-medium text-ink hover:bg-elevated">
                  <Pencil aria-hidden="true" className="size-4" />{t("review.open_in_editor")}
                </Link>
                {can(A.open) && <Button variant="primary" disabled={open.isPending} onClick={() => open.mutate()}><Eye aria-hidden="true" className="size-4" />{t("review.start_review")}</Button>}
              </div>
            </div>
            <ErrorLine error={open.error} />
            <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
              <div className="min-w-0 space-y-5">
                <Player w={w} player={player} onTime={setNow} onVersion={(n) => setParams(n === w.versions[w.versions.length - 1]?.version ? {} : { v: String(n) }, { replace: true })} />
                {w.filmstrip && <Filmstrip w={w} onSeek={seek} />}
                <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                  <Checklist w={w} onDone={refresh} />
                  <Feedback w={w} now={now} onSeek={seek} onDone={refresh} />
                </div>
              </div>
              <aside className="space-y-5">
                <StatusCard w={w} onDone={refresh} />
                <AssetDetails w={w} />
                <History w={w} />
                <RelatedFilesCard w={w} />
                {w.review?.notes_visible && <ReviewerNotes w={w} onDone={refresh} />}
              </aside>
            </div>
          </div>
        );
      }}
    </QueryState>
  );
}

function Player({ w, player, onTime, onVersion }: { w: WS; player: RefObject<HTMLVideoElement | null>; onTime: (ms: number) => void; onVersion: (n: number) => void }) {
  const { t } = useTranslation();
  const [quality, setQuality] = useState<"proxy" | "original">("proxy");
  const isVideo = (w.asset?.mime_type ?? "").startsWith("video/");
  const src = quality === "original" ? w.media.original : w.media.proxy ?? w.media.original;
  const status = w.review?.status ?? "PENDING";
  return (
    <Card className="overflow-hidden p-0">
      <div className="relative bg-black">
        {isVideo && src ? (
          <video ref={player} key={src} src={src} poster={w.media.poster} controls playsInline className="aspect-video w-full" aria-label={t("review.player", { n: w.selected_version })}
            onTimeUpdate={(e) => onTime(Math.round(e.currentTarget.currentTime * 1000))} />
        ) : w.media.poster || w.media.original ? (
          <img src={w.media.poster ?? w.media.original} alt={t("review.player", { n: w.selected_version })} className="mx-auto aspect-video max-h-[60vh] w-full object-contain" />
        ) : (
          <div className="flex aspect-video items-center justify-center text-[14px] text-white/80">{t("assets.processing_pending")}</div>
        )}
        <div className="absolute right-3 top-3 flex items-center gap-2">
          <label className="sr-only" htmlFor="version-select">{t("review.version")}</label>
          <select id="version-select" value={w.selected_version} onChange={(e) => onVersion(Number(e.target.value))}
            className="h-9 rounded-[var(--radius-control)] border border-white/20 bg-black/60 px-3 text-[13px] text-white">
            {w.versions.map((x) => <option key={x.version} value={x.version}>{t(x.current ? "review.version_current" : "editor.version_short", { n: x.version })}</option>)}
          </select>
          <span className="rounded-full bg-black/60 px-3 py-1.5 text-[12px] font-semibold uppercase text-white">{t(`review.status_${status.toLowerCase()}`)}</span>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <div role="tablist" aria-label={t("review.versions")} className="flex gap-1">
          {w.versions.map((x) => (
            <button key={x.version} type="button" role="tab" aria-selected={x.version === w.selected_version} onClick={() => onVersion(x.version)}
              className="rounded-[var(--radius-control)] px-3 py-1.5 text-[13px] text-ink-2 aria-selected:bg-[var(--accent-primary-soft)] aria-selected:font-semibold aria-selected:text-violet-text">
              {t("editor.version_short", { n: x.version })}{x.current ? ` · ${t("review.current")}` : ""}
            </button>
          ))}
        </div>
        {isVideo && w.media.original && (
          <div role="radiogroup" aria-label={t("review.quality")} className="flex overflow-hidden rounded-[var(--radius-control)] border border-line text-[12px]">
            {QUALITIES.map((k) => (
              <label key={k} className={`cursor-pointer px-3 py-1.5 ${quality === k ? "bg-[var(--accent-primary-soft)] font-semibold text-violet-text" : "text-ink-2"}`}>
                <input type="radio" className="sr-only" name="quality" checked={quality === k} onChange={() => setQuality(k)} />{t(`review.quality_${k}`)}
              </label>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

function Filmstrip({ w, onSeek }: { w: WS; onSeek: (ms: number) => void }) {
  const { t } = useTranslation();
  const f = w.filmstrip;
  if (!f) return null;
  const n = Math.max(1, f.frames.length);
  return (
    <ol aria-label={t("review.filmstrip")} className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(n, 6)}, minmax(0, 1fr))` }}>
      {f.frames.filter((_, i) => n <= 6 || i % Math.ceil(n / 6) === 0).slice(0, 6).map((fr) => (
        <li key={fr.frame}>
          <button type="button" onClick={() => onSeek(fr.timecode_ms)} aria-label={t("review.seek_to", { tc: timecode(fr.timecode_ms) })}
            className="relative block aspect-video w-full overflow-hidden rounded-[var(--radius-control)] border border-line bg-soft"
            style={{ backgroundImage: `url(${f.url})`, backgroundSize: `${n * 100}% 100%`, backgroundPosition: `${n > 1 ? (fr.frame / (n - 1)) * 100 : 0}% 0` }}>
            <span className="absolute bottom-1 right-1 rounded bg-black/70 px-1.5 py-0.5 text-[11px] font-medium text-white">{timecode(fr.timecode_ms)}</span>
          </button>
        </li>
      ))}
    </ol>
  );
}

function Checklist({ w, onDone }: { w: WS; onDone: () => void }) {
  const { t } = useTranslation();
  const tick = useMutation({
    mutationFn: ({ id, checked }: { id: string; checked: boolean }) => call(api.POST("/app/v1/reviews/{display_id}/checklist/{item_id}", {
      params: { path: { display_id: w.display_id, item_id: id } }, body: { checked } })),
    onSuccess: onDone,
  });
  const c = w.checklist;
  const can = w.allowed_actions.includes(A.tick);
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between">
        <h2 className="text-[16px] font-semibold text-ink">{t("review.checklist")}</h2>
        {c && <span className="text-[13px] text-ink-2">{t("review.checklist_progress", { done: c.done, total: c.total })}</span>}
      </div>
      {!c ? <p className="mt-2 text-[13px] text-ink-2">{t("review.checklist_after_open")}</p> : (
        <>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-soft" role="progressbar" aria-label={t("review.checklist")} aria-valuemin={0} aria-valuemax={c.total} aria-valuenow={c.done}>
            <div className="h-full bg-[image:var(--gradient-primary-button)]" style={{ width: `${c.total ? (c.done / c.total) * 100 : 0}%` }} />
          </div>
          <ul className="mt-3 space-y-1">
            {c.items.map((i) => {
              const label = i.key ? t(`review.check_${i.key}`) : i.label;
              return (
                <li key={i.id}>
                  <label className={`flex items-start gap-3 rounded-[var(--radius-control)] p-2 ${can ? "cursor-pointer hover:bg-elevated" : ""}`}>
                    <input type="checkbox" className="sr-only" checked={i.checked} disabled={!can || tick.isPending} onChange={(e) => tick.mutate({ id: i.id, checked: e.target.checked })} />
                    {i.checked ? <CircleCheck aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-[var(--status-success)]" /> : <Circle aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-ink-2" />}
                    <span className="min-w-0">
                      <span className="block text-[14px] font-medium text-ink">{label}</span>
                      {i.key && <span className="block text-[12px] text-ink-2">{t(`review.check_${i.key}_hint`)}</span>}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
          <p className="mt-2 text-[12px] text-ink-2">{t("review.checklist_advisory")}</p>
        </>
      )}
      <ErrorLine error={tick.error} />
    </Card>
  );
}

function Feedback({ w, now, onSeek, onDone }: { w: WS; now: number; onSeek: (ms: number) => void; onDone: () => void }) {
  const { t, i18n } = useTranslation();
  const [body, setBody] = useState("");
  const [withTime, setWithTime] = useState(true);
  const [replyTo, setReplyTo] = useState<CommentT | null>(null);
  const post = useMutation({
    mutationFn: () => call(api.POST("/app/v1/reviews/{display_id}/comments", {
      params: { path: { display_id: w.display_id } },
      body: { body: body.trim(), timecode_ms: replyTo || !withTime ? null : now, parent_id: replyTo?.id ?? null } })),
    onSuccess: () => { setBody(""); setReplyTo(null); onDone(); },
  });
  const resolve = useMutation({
    mutationFn: (id: string) => call(api.POST("/app/v1/reviews/{display_id}/comments/{comment_id}/resolve", { params: { path: { display_id: w.display_id, comment_id: id } } })),
    onSuccess: onDone,
  });
  const canPost = w.allowed_actions.includes(A.comment) || w.allowed_actions.includes(A.reply);
  const onlyReply = !w.allowed_actions.includes(A.comment);
  const top = w.comments.filter((c) => !c.parent_id);
  const replies = (id: string) => w.comments.filter((c) => c.parent_id === id);
  const item = (c: CommentT, nested = false) => (
    <li key={c.id} className={nested ? "ml-8 mt-2" : "py-3"}>
      <div className="flex flex-wrap items-center gap-2 text-[13px]">
        <span className="font-semibold text-ink">{c.author}</span>
        <span className="rounded-full border border-line px-2 py-0.5 text-[11px] text-ink-2">{t(`role.${c.role}`)}</span>
        <span className="text-ink-2">{c.created_at ? formatRelative(secondsSince(c.created_at), i18n.language) : ""}</span>
        {c.timecode_ms != null && (
          <button type="button" onClick={() => onSeek(c.timecode_ms ?? 0)} className="rounded bg-[var(--accent-primary-soft)] px-1.5 py-0.5 text-[12px] font-semibold text-violet-text">{timecode(c.timecode_ms)}</button>
        )}
        {c.status === "RESOLVED" && <span className="flex items-center gap-1 text-[12px] text-[var(--status-success)]"><Check aria-hidden="true" className="size-3.5" />{t("review.resolved")}</span>}
      </div>
      <p className="mt-1 whitespace-pre-wrap text-[14px] text-ink">{c.body}</p>
      {!nested && canPost && (
        <div className="mt-1 flex gap-3 text-[12px]">
          <button type="button" className="text-violet-text hover:underline" onClick={() => setReplyTo(c)}>{t("review.reply")}</button>
          {c.status === "OPEN" && <button type="button" className="text-ink-2 hover:text-ink" onClick={() => resolve.mutate(c.id)}>{t("review.resolve")}</button>}
        </div>
      )}
      {!nested && replies(c.id).length > 0 && <ul>{replies(c.id).map((r) => item(r, true))}</ul>}
    </li>
  );
  return (
    <Card className="flex flex-col p-5">
      <h2 className="flex items-center gap-2 text-[16px] font-semibold text-ink"><MessageSquare aria-hidden="true" className="size-4 text-violet" />{t("review.feedback", { count: w.comments.length })}</h2>
      {top.length === 0 ? <p className="mt-2 text-[13px] text-ink-2">{t("review.no_comments")}</p> : <ul className="mt-1 divide-y divide-[var(--border-default)]">{[...top].reverse().map((c) => item(c))}</ul>}
      {canPost && (
        <form className="mt-3 space-y-2 border-t border-line pt-3" onSubmit={(e) => { e.preventDefault(); if (body.trim() && (!onlyReply || replyTo)) post.mutate(); }}>
          {replyTo && <p className="text-[12px] text-ink-2">{t("review.replying_to", { name: replyTo.author ?? "" })} <button type="button" className="underline" onClick={() => setReplyTo(null)}>{t("common.cancel")}</button></p>}
          {onlyReply && !replyTo && <p className="text-[12px] text-ink-2">{t("review.editor_reply_hint")}</p>}
          <label htmlFor="comment-body" className="sr-only">{t("review.add_comment")}</label>
          <textarea id="comment-body" rows={2} value={body} onChange={(e) => setBody(e.target.value)} placeholder={t("review.add_comment")} disabled={onlyReply && !replyTo}
            className="w-full rounded-[var(--radius-control)] border border-line bg-surface p-2 text-[14px] text-ink placeholder:text-ink-2" />
          <div className="flex items-center justify-between gap-2">
            {!replyTo && !onlyReply ? (
              <label className="flex items-center gap-2 text-[12px] text-ink-2">
                <input type="checkbox" checked={withTime} onChange={(e) => setWithTime(e.target.checked)} />{t("review.at_time", { tc: timecode(now) })}
              </label>
            ) : <span />}
            <Button type="submit" variant="primary" className="h-9" disabled={!body.trim() || post.isPending || (onlyReply && !replyTo)}>{t("review.post")}</Button>
          </div>
          <ErrorLine error={post.error ?? resolve.error} />
        </form>
      )}
    </Card>
  );
}

function StatusCard({ w, onDone }: { w: WS; onDone: () => void }) {
  const { t } = useTranslation();
  const [dialog, setDialog] = useState<null | "approve" | "revision" | "reject">(null);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [priority, setPriority] = useState<"NORMAL" | "URGENT">("NORMAL");
  const rv = w.review?.row_version ?? 0;
  const done = () => { setDialog(null); setReason(""); setNote(""); onDone(); };
  const approve = useMutation({ mutationFn: () => call(api.POST("/app/v1/reviews/{display_id}/approve", { params: { path: { display_id: w.display_id } }, body: { row_version: rv } })), onSuccess: done });
  const revision = useMutation({ mutationFn: () => call(api.POST("/app/v1/reviews/{display_id}/revision", { params: { path: { display_id: w.display_id } },
    body: { reason_code: reason as (typeof REVISION_REASONS)[number], general_note: note.trim(), priority, row_version: rv } })), onSuccess: done });
  const reject = useMutation({ mutationFn: () => call(api.POST("/app/v1/reviews/{display_id}/reject", { params: { path: { display_id: w.display_id } },
    body: { reason_code: reason as (typeof REJECT_REASONS)[number], note: note.trim(), row_version: rv } })), onSuccess: done });
  const can = (a: string) => w.allowed_actions.includes(a);
  const status = w.review?.status ?? (w.job_status === "SUBMITTED" ? "PENDING" : w.job_status);
  const unchecked = w.checklist?.unchecked ?? [];
  const select = "h-10 w-full rounded-[var(--radius-control)] border border-line bg-surface px-3 text-[14px] text-ink";
  const area = "w-full rounded-[var(--radius-control)] border border-line bg-surface p-3 text-[14px] text-ink";
  return (
    <Card className="p-5">
      <h2 className="text-[16px] font-semibold text-ink">{t("review.status_card")}</h2>
      <div className="mt-3 flex items-center gap-3">
        <span className="flex size-11 items-center justify-center rounded-full bg-[var(--accent-secondary-soft)] text-magenta"><Eye aria-hidden="true" className="size-5" /></span>
        <div>
          <StatusChip tone={STATUS_TONE[status] ?? "neutral"} icon={null} label={t(`review.status_${status.toLowerCase()}`)} />
          <p className="mt-1 text-[12px] text-ink-2">{t("review.revisions_used", { used: w.revisions_used, limit: w.revision_limit })}</p>
        </div>
      </div>
      {w.review?.decision_reason && <p className="mt-3 text-[13px] text-ink-2">{w.review.decision_reason}</p>}
      {(can(A.approve) || can(A.revision) || can(A.reject)) && (
        <div className="mt-4 grid grid-cols-2 gap-2">
          <button type="button" onClick={() => setDialog("approve")} className="inline-flex h-10 items-center justify-center gap-2 rounded-[var(--radius-control)] border border-[var(--status-success)] bg-[var(--status-success-soft)] text-sm font-medium text-[var(--status-success)]">
            <Check aria-hidden="true" className="size-4" />{t("review.approve")}
          </button>
          <button type="button" onClick={() => setDialog("revision")} className="inline-flex h-10 items-center justify-center gap-2 rounded-[var(--radius-control)] border border-[var(--accent-primary)] bg-[var(--accent-primary-soft)] text-sm font-medium text-violet-text">
            <RotateCcw aria-hidden="true" className="size-4" />{t("review.request_revision")}
          </button>
          <button type="button" onClick={() => setDialog("reject")} className="col-span-2 inline-flex h-10 items-center justify-center gap-2 rounded-[var(--radius-control)] border border-line text-sm font-medium text-[var(--status-danger)] hover:bg-elevated">
            <XCircle aria-hidden="true" className="size-4" />{t("review.reject")}
          </button>
        </div>
      )}
      <Dialog open={dialog === "approve"} onOpenChange={(o) => !o && setDialog(null)} title={t("review.approve_title", { n: w.selected_version })} description={t("review.approve_body")}>
        {unchecked.length > 0 ? (
          <div role="status" className="rounded-[var(--radius-control)] border border-line p-3">
            <p className="text-[14px] font-medium text-ink">{t("review.unchecked_count", { count: unchecked.length, total: w.checklist?.total ?? 0 })}</p>
            <ul className="mt-2 space-y-1 text-[13px]">
              {unchecked.map((u) => (
                <li key={u.key ?? u.label} className={u.key === CLAIMS ? "flex items-center gap-2 font-semibold text-[var(--status-warning)]" : "text-ink"}>
                  {u.key === CLAIMS && <AlertTriangle aria-hidden="true" className="size-4" />}{u.key ? t(`review.check_${u.key}`) : u.label}
                </li>
              ))}
            </ul>
            <p className="mt-2 text-[12px] text-ink-2">{t("review.checklist_advisory")}</p>
          </div>
        ) : <p className="text-[14px] text-[var(--status-success)]">{t("review.all_checked")}</p>}
        <ErrorLine error={approve.error} />
        <div className="mt-6 flex justify-end gap-2"><Button variant="ghost" onClick={() => setDialog(null)}>{t("common.cancel")}</Button>
          <Button variant="primary" disabled={approve.isPending} onClick={() => approve.mutate()}>{t("review.approve_confirm")}</Button></div>
      </Dialog>
      <Dialog open={dialog === "revision"} onOpenChange={(o) => !o && setDialog(null)} title={t("review.revision_title")} description={t("review.revision_body", { used: w.revisions_used, limit: w.revision_limit })}>
        <label htmlFor="rev-reason" className="mb-1 block text-[13px] font-medium text-ink-2">{t("review.reason")}</label>
        <select id="rev-reason" className={select} value={reason} onChange={(e) => setReason(e.target.value)}>
          <option value="">{t("requests.choose_reason")}</option>
          {REVISION_REASONS.map((r) => <option key={r} value={r}>{t(`review.rev_${r.toLowerCase()}`)}</option>)}
        </select>
        <label htmlFor="rev-note" className="mb-1 mt-3 block text-[13px] font-medium text-ink-2">{t("review.revision_note")}</label>
        <textarea id="rev-note" rows={4} className={area} value={note} onChange={(e) => setNote(e.target.value)} />
        <fieldset className="mt-3"><legend className="mb-1 text-[13px] font-medium text-ink-2">{t("review.priority")}</legend>
          <div className="flex gap-3 text-[14px] text-ink">{(["NORMAL", "URGENT"] as const).map((p) => (
            <label key={p} className="flex items-center gap-2"><input type="radio" name="rev-priority" checked={priority === p} onChange={() => setPriority(p)} />{t(`requests.priority_${p.toLowerCase()}`)}</label>))}</div>
        </fieldset>
        {w.revisions_used >= w.revision_limit && <p className="mt-3 flex items-center gap-2 text-[13px] text-[var(--status-warning)]"><AlertTriangle aria-hidden="true" className="size-4" />{t("review.over_limit")}</p>}
        <ErrorLine error={revision.error} />
        <div className="mt-6 flex justify-end gap-2"><Button variant="ghost" onClick={() => setDialog(null)}>{t("common.cancel")}</Button>
          <Button variant="primary" disabled={!reason || !note.trim() || revision.isPending} onClick={() => revision.mutate()}>{t("review.request_revision")}</Button></div>
      </Dialog>
      <Dialog open={dialog === "reject"} onOpenChange={(o) => !o && setDialog(null)} title={t("review.reject_title")} description={t("review.reject_body")}>
        <label htmlFor="rej-reason" className="mb-1 block text-[13px] font-medium text-ink-2">{t("review.reason")}</label>
        <select id="rej-reason" className={select} value={reason} onChange={(e) => setReason(e.target.value)}>
          <option value="">{t("requests.choose_reason")}</option>
          {REJECT_REASONS.map((r) => <option key={r} value={r}>{t(`review.rej_${r.toLowerCase()}`)}</option>)}
        </select>
        <label htmlFor="rej-note" className="mb-1 mt-3 block text-[13px] font-medium text-ink-2">{t("review.reject_note")}</label>
        <textarea id="rej-note" rows={4} className={area} value={note} onChange={(e) => setNote(e.target.value)} />
        <ErrorLine error={reject.error} />
        <div className="mt-6 flex justify-end gap-2"><Button variant="ghost" onClick={() => setDialog(null)}>{t("common.cancel")}</Button>
          <Button variant="primary" disabled={!reason || !note.trim() || reject.isPending} onClick={() => reject.mutate()}>{t("review.reject_confirm")}</Button></div>
      </Dialog>
    </Card>
  );
}

function Row_({ label, children }: { label: string; children: ReactNode }) {
  return <div className="flex gap-3 py-1.5"><dt className="w-28 shrink-0 text-ink-2">{label}</dt><dd className="min-w-0 text-ink">{children ?? "—"}</dd></div>;
}

function AssetDetails({ w }: { w: WS }) {
  const { t } = useTranslation();
  const a = w.asset;
  if (!a) return null;
  const aspect = a.width && a.height ? `${a.width}×${a.height}${a.orientation ? ` · ${t(`review.orientation_${a.orientation}`)}` : ""}` : null;
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between"><h2 className="text-[16px] font-semibold text-ink">{t("review.asset_details")}</h2>
        <Link to={`/assets?asset=${a.id}`} className="text-[13px] text-violet-text hover:underline">{t("review.view_in_library")}</Link></div>
      <p className="mt-2 truncate text-[14px] font-medium text-ink">{a.file_name}</p>
      <dl className="mt-1 text-[13px]">
        <Row_ label={t("review.type")}>{a.mime_type}</Row_>
        <Row_ label={t("editor.duration")}>{a.duration_s ? timecode(a.duration_s * 1000) : null}</Row_>
        <Row_ label={t("review.resolution")}>{aspect}</Row_>
        <Row_ label={t("review.size")}>{formatBytes(a.file_size)}</Row_>
        <Row_ label={t("review.version")}>{t(w.is_current ? "review.version_current" : "editor.version_short", { n: w.selected_version })}</Row_>
      </dl>
      {w.media.original && (
        <a href={w.media.original} download={a.file_name} className="mt-3 inline-flex items-center gap-2 text-[13px] text-violet-text hover:underline"><Download aria-hidden="true" className="size-4" />{t("assets.download_original")}</a>
      )}
    </Card>
  );
}

function History({ w }: { w: WS }) {
  const { t, i18n } = useTranslation();
  const me = useSession();
  return (
    <Card className="p-5">
      <h2 className="text-[16px] font-semibold text-ink">{t("review.history")}</h2>
      <ol className="mt-3 space-y-3 border-l border-line pl-4">
        {w.history.map((h, i) => (
          <li key={i} className="relative text-[13px]">
            <span aria-hidden="true" className={`absolute -left-[21px] top-1 size-2.5 rounded-full ${i === 0 ? "bg-[var(--accent-primary)]" : "bg-soft"}`} />
            <div className="flex flex-wrap items-center gap-2">
              {h.version != null && <span className="rounded bg-elevated px-1.5 text-[11px] font-semibold text-ink">{t("editor.version_short", { n: h.version })}</span>}
              <span className="font-medium text-ink">{t(`review.history_${h.kind}`)}</span>
            </div>
            <p className="text-ink-2">{h.by}{h.at ? ` · ${formatDateTime(new Date(h.at), i18n.language, me.time_zone)}` : ""}</p>
            {h.note && <p className="mt-0.5 text-ink">{h.reason_code ? `${t(`review.rev_${h.reason_code.toLowerCase()}`, { defaultValue: h.reason_code })}: ` : ""}{h.note}</p>}
          </li>
        ))}
      </ol>
    </Card>
  );
}

function RelatedFilesCard({ w }: { w: WS }) {
  const { t } = useTranslation();
  return (
    <Card className="p-5">
      <h2 className="text-[16px] font-semibold text-ink">{t("review.related_files", { count: w.related_files.length })}</h2>
      {w.related_files.length === 0 ? <p className="mt-2 text-[13px] text-ink-2">{t("assets.no_files")}</p> : (
        <ul className="mt-2 space-y-1.5 text-[13px]">
          {w.related_files.map((f) => (
            <li key={f.id} className="flex items-center gap-2">
              <FileText aria-hidden="true" className="size-4 shrink-0 text-ink-2" />
              <a href={f.url} target="_blank" rel="noreferrer noopener" className="min-w-0 flex-1 truncate text-violet-text hover:underline">{f.file_name}</a>
              <span className="shrink-0 text-ink-2">{formatBytes(f.file_size)}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function ReviewerNotes({ w, onDone }: { w: WS; onDone: () => void }) {
  const { t } = useTranslation();
  const [notes, setNotes] = useState(w.review?.internal_notes ?? "");
  const editable = w.allowed_actions.includes(A.notes);
  const save = useMutation({
    mutationFn: () => call(api.PUT("/app/v1/reviews/{display_id}/notes", { params: { path: { display_id: w.display_id } }, body: { notes: notes.trim() || null, row_version: w.review?.row_version ?? 0 } })),
    onSuccess: onDone,
  });
  return (
    <Card className="p-5">
      <h2 className="text-[16px] font-semibold text-ink">{t("review.reviewer_notes")}</h2>
      <label htmlFor="reviewer-notes" className="sr-only">{t("review.reviewer_notes")}</label>
      <textarea id="reviewer-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} disabled={!editable} placeholder={t("review.notes_placeholder")}
        onBlur={() => { if (editable && notes !== (w.review?.internal_notes ?? "")) save.mutate(); }}
        className="mt-2 w-full rounded-[var(--radius-control)] border border-line bg-surface p-2 text-[13px] text-ink placeholder:text-ink-2 disabled:bg-elevated" />
      <p className="text-[12px] text-ink-2">{t("review.notes_private")}</p>
      <ErrorLine error={save.error} />
    </Card>
  );
}
